import { ChangeDetectionStrategy, Component, inject, Input, OnChanges } from '@angular/core';
import {
  ComparisonStatus,
  ComparisonType,
  ConsumptionComparisonService,
  ConsumptionComparisonWidths,
  ConsumptionTimeframe,
  getSelectedConsumptionForMeter,
  IConsumption,
  IConsumptionInfoItem,
  IConsumptionState,
  ImmomioIconName,
  NgChanges,
} from '@resident-nx/shared';
import { Observable, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { ConsumptionRowWebComponent } from '../../atoms/consumption-row/consumption-row.component';
import { IconWebComponent } from '@resident-nx/web/ui';
import { InfoModalWebComponent } from '../info-modal/info-modal.component';
import { ModalService } from '../modal/modal.service';

@Component({
  standalone: true,
  selector: 'rs-web-consumption-comparison',
  templateUrl: 'consumption-comparison.component.html',
  styleUrls: ['consumption-comparison.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ConsumptionRowWebComponent,
    NgClass,
    AsyncPipe,
    DatePipe,
    TranslateModule,
    IconWebComponent,
  ],
})
export class ConsumptionComparisonWebComponent implements OnChanges {
  #modalService = inject(ModalService);
  public readonly ComparisonType = ComparisonType;
  public readonly ImmomioIconName = ImmomioIconName;
  @Input() date = new Date();
  @Input() timeframe: ConsumptionTimeframe.MONTH | ConsumptionTimeframe.YEAR;
  @Input() meter: IConsumptionInfoItem;
  @Input() showLegend = true;

  public selectedConsumption$: Observable<IConsumption>;
  public backgroundWidths: ConsumptionComparisonWidths = {
    consumption: 0,
    pastConsumption: 0,
    benchmark: 0,
  };
  public comparisonStatus: ComparisonStatus;

  public dateFormat: string;

  constructor(
    private store: Store<IConsumptionState>,
    private comparisonService: ConsumptionComparisonService
  ) {}

  ngOnChanges(changes: NgChanges<ConsumptionComparisonWebComponent>): void {
    if (changes.timeframe) {
      this.dateFormat = this.getDateFormatByTimeframe(this.timeframe);
    }
    if (changes.meter) {
      this.selectedConsumption$ = this.store
        .select(getSelectedConsumptionForMeter(this.meter.deviceId))
        .pipe(
          tap(selectedConsumption => {
            if (!selectedConsumption) {
              return;
            }

            this.backgroundWidths =
              this.comparisonService.calculateBackgroundWidth(selectedConsumption);

            this.comparisonStatus = this.comparisonService.getComparisonStatus(selectedConsumption);
          })
        );
    }
  }

  openBenchmarkInfoModal(): void {
    const modalRef = this.#modalService.open(InfoModalWebComponent, { centered: true });
    modalRef.componentInstance.title = 'consumption_comparison.info_title_l';
    modalRef.componentInstance.text = 'consumption_comparison.info_desc_l';
  }

  getDateFormatByTimeframe(timeframe: ConsumptionTimeframe.YEAR | ConsumptionTimeframe.MONTH) {
    if (timeframe === ConsumptionTimeframe.YEAR) {
      return 'MMMM yyyy';
    }

    return 'd. MMMM';
  }

  protected readonly ConsumptionTimeframe = ConsumptionTimeframe;
}
